import Icon from '@atoms/Icon/Icon';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { forwardRef, KeyboardEvent, useImperativeHandle, useRef } from 'react';

interface Props {
  readonly direction:
    | HorizontalDirectionEnum.Left
    | HorizontalDirectionEnum.Right;
  readonly id?: string;
  readonly disabled?: boolean;
  readonly hasBackgroundColor?: boolean;
  readonly sizeClassName?: string;
  readonly iconSize?: string;
  readonly enabledArrowColor?: string;
  readonly onClick?: (e?: KeyboardEvent<HTMLButtonElement>) => void;
  readonly additionalDataLayerProperties?: Record<string, string>;
  readonly isLightMode?: boolean;
}

export interface NavButtonRef {
  focus: () => void;
  disabled: boolean;
}

const NavButton = forwardRef<NavButtonRef, Props>(
  (
    {
      additionalDataLayerProperties,
      direction,
      id,
      disabled = false,
      hasBackgroundColor = true,
      sizeClassName = 'h-10 w-10',
      iconSize,
      enabledArrowColor = 'text-black-100',
      isLightMode,
      onClick,
    },
    ref
  ) => {
    const { nextAriaLabel, previousAriaLabel } = useAriaLabels();
    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => buttonRef.current?.focus(),
      disabled: !!buttonRef.current?.disabled,
    }));

    return (
      <button
        ref={buttonRef}
        id={`${
          direction === HorizontalDirectionEnum.Left ? 'prevBtn' : 'nextBtn'
        }-${id}`}
        className={classNames(
          'flex select-none items-center justify-center rounded-full',
          sizeClassName,
          {
            'animate-arrow-left':
              direction === HorizontalDirectionEnum.Left && !disabled,
            'animate-arrow-right':
              direction === HorizontalDirectionEnum.Right && !disabled,
            'cursor-pointer': !disabled,
            'cursor-default': disabled,
            'bg-yellow active:bg-yellow-500 xl:hover:bg-yellow-100 xl:active:bg-yellow-500':
              hasBackgroundColor && !disabled,
            [`bg-grey ${isLightMode ? '' : 'dark:bg-grey-600'}`]:
              hasBackgroundColor && disabled,
          }
        )}
        aria-label={
          direction === HorizontalDirectionEnum.Left
            ? previousAriaLabel
            : nextAriaLabel
        }
        disabled={disabled}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-testid={
          direction === HorizontalDirectionEnum.Left
            ? 'previous-slide'
            : 'next-slide'
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') onClick?.(e);
        }}
        onClick={() => {
          onClick?.();
          updateDataLayer({
            event: 'carousel',
            content_type: 'tab_change',
            link_text:
              direction === HorizontalDirectionEnum.Left ? 'Previous' : 'Next',
            ...additionalDataLayerProperties,
          });
        }}
      >
        <Icon
          name={`arrow_${
            direction === HorizontalDirectionEnum.Left ? 'left' : 'right'
          }`}
          colorClassName={
            disabled
              ? `text-grey-400 ${isLightMode ? '' : 'dark:text-grey-700'}`
              : enabledArrowColor
          }
          size={iconSize}
        />
      </button>
    );
  }
);

export default NavButton;
